/**
 * G2eGente - Gestão de Treinamentos Obrigatórios
 * Controle de treinamentos por cargo com validade e lembretes
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { trainingsAPI, employeeTrainingsAPI, jobPositionsAPI, employeesAPI, turmasAPI, trainingGridsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, 
  Search, 
  GraduationCap,
  Loader2,
  Edit,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Calendar,
  FileText,
  Bell,
  X,
  Filter,
  ClipboardList,
  UserCheck,
  Layers,
  ListChecks
} from 'lucide-react';
import { toast } from 'sonner';

// Import new tab components
import TurmasTab from './TurmasTab';
import QualificacaoTab from './QualificacaoTab';
import LNTTab from './LNTTab';
import { CargoClassificationDialog } from '../../components/trainings/CargoClassificationDialog';
import { TrainingReclassifyDialog } from '../../components/trainings/TrainingReclassifyDialog';

const CATEGORIAS = {
  'obrigatorio': 'Obrigatório (NRs)',
  'seguranca': 'Segurança do Trabalho',
  'tecnico': 'Técnico',
  'comportamental': 'Comportamental',
  'desenvolvimento': 'Desenvolvimento',
  'integracao': 'Integração',
  'reciclagem': 'Reciclagem',
  'eventual': 'Eventual'
};

export default function Treinamentos() {
  const [loading, setLoading] = useState(true);
  const [trainings, setTrainings] = useState([]);
  const [categories, setCategories] = useState({});
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [activeTab, setActiveTab] = useState('trainings');
  
  // Modals
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showExpiringModal, setShowExpiringModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [expiringData, setExpiringData] = useState(null);
  
  // Import from job descriptions
  const [extractedTrainings, setExtractedTrainings] = useState([]);
  const [selectedImports, setSelectedImports] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Turmas state
  const [turmas, setTurmas] = useState([]);
  const [showTurmaModal, setShowTurmaModal] = useState(false);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [generatingTurmas, setGeneratingTurmas] = useState(false);
  const [trainingGrids, setTrainingGrids] = useState([]);
  
  // Form state
  const [trainingForm, setTrainingForm] = useState({
    nome: '',
    descricao: '',
    categoria: 'obrigatorio',
    carga_horaria: '',
    validade_meses: '',
    obrigatorio: true,
    empresa_wide: false,
    cargos_vinculados: [],
    requisitos_legais: '',
    fornecedor: '',
    custo_estimado: ''
  });
  
  const [registerForm, setRegisterForm] = useState({
    employee_id: '',
    training_id: '',
    data_realizacao: '',
    certificado_url: '',
    observacoes: '',
    nota: ''
  });
  
  const [turmaForm, setTurmaForm] = useState({
    training_id: '',
    nome: '',
    data_inicio: '',
    data_fim: '',
    instrutor: '',
    local: 'presencial',
    local_detalhes: '',
    capacidade_maxima: 30,
    observacoes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [trainingsRes, positionsRes, employeesRes, turmasRes, gridsRes] = await Promise.all([
        trainingsAPI.list(),
        jobPositionsAPI.getAll(),
        employeesAPI.getAll({ situacao: 'ativo' }),
        turmasAPI.getAll(),
        trainingGridsAPI.getAll()
      ]);
      setTrainings(trainingsRes.data);
      setTurmas(turmasRes.data || []);
      setPositions(positionsRes.data);
      setEmployees(employeesRes.data);
      setTrainingGrids(gridsRes.data || []);
      setCategories(CATEGORIAS);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTraining = async () => {
    if (!trainingForm.nome.trim()) {
      toast.error('Nome do treinamento é obrigatório');
      return;
    }

    try {
      const data = {
        ...trainingForm,
        carga_horaria: trainingForm.carga_horaria ? parseInt(trainingForm.carga_horaria) : null,
        validade_meses: trainingForm.validade_meses ? parseInt(trainingForm.validade_meses) : null,
        custo_estimado: trainingForm.custo_estimado ? parseFloat(trainingForm.custo_estimado) : null
      };

      if (editingTraining) {
        await trainingsAPI.update(editingTraining.id, data);
        toast.success('Treinamento atualizado');
      } else {
        await trainingsAPI.create(data);
        toast.success('Treinamento criado');
      }
      
      setShowTrainingModal(false);
      resetTrainingForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleRegisterTraining = async () => {
    if (!registerForm.employee_id || !registerForm.training_id || !registerForm.data_realizacao) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const data = {
        ...registerForm,
        nota: registerForm.nota ? parseFloat(registerForm.nota) : null
      };
      await employeeTrainingsAPI.create(data);
      toast.success('Treinamento registrado');
      setShowRegisterModal(false);
      resetRegisterForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar');
    }
  };

  const handleDelete = async (training) => {
    if (!confirm(`Excluir "${training.nome}"?`)) return;
    
    try {
      await trainingsAPI.delete(training.id);
      toast.success('Treinamento excluído');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // Extrair treinamentos das descrições de cargo
  const [ignoredCounts, setIgnoredCounts] = useState({ requisitos: 0, competencias: 0 });
  const [showCargoView, setShowCargoView] = useState(false);
  const [showReclassify, setShowReclassify] = useState(false);

  const handleExtractFromDescriptions = async () => {
    setImporting(true);
    try {
      // Classificação no servidor: apenas itens do tipo Treinamento são importáveis
      const res = await jobPositionsAPI.getClassification();
      const extractedSet = new Map();
      let reqCount = 0;
      let compCount = 0;
      const keyOf = (n) => n.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[.;,:\s]+$/, '').trim();

      (res.data || []).forEach(pos => {
        reqCount += pos.requisitos.length;
        compCount += pos.competencias.length;
        pos.treinamentos.forEach(t => {
          if (!extractedSet.has(keyOf(t.nome))) {
            extractedSet.set(keyOf(t.nome), { nome: t.nome, origem: pos.titulo, categoria: t.categoria, sugerido: true });
          }
        });
      });
      setIgnoredCounts({ requisitos: reqCount, competencias: compCount });
      
      // Filtrar treinamentos que já existem
      const existingNames = trainings.map(t => keyOf(t.nome));
      const newTrainings = Array.from(extractedSet.values())
        .filter(t => !existingNames.includes(keyOf(t.nome)));
      
      setExtractedTrainings(newTrainings);
      setSelectedImports(newTrainings.map((_, i) => i));
      setShowImportModal(true);
      
      if (newTrainings.length === 0) {
        toast.info('Não foram encontrados novos treinamentos nas descrições de cargo');
      }
    } catch (error) {
      console.error('Error extracting trainings:', error);
      toast.error('Erro ao extrair treinamentos das descrições');
    } finally {
      setImporting(false);
    }
  };

  // Confirmar importação de treinamentos
  const handleConfirmImport = async () => {
    const toImport = extractedTrainings.filter((_, i) => selectedImports.includes(i));
    
    if (toImport.length === 0) {
      toast.error('Selecione pelo menos um treinamento para importar');
      return;
    }
    
    setImporting(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const training of toImport) {
      try {
        await trainingsAPI.create({
          nome: training.nome,
          categoria: training.categoria,
          descricao: `Extraído da descrição de cargo: ${training.origem}`,
          obrigatorio: training.categoria === 'obrigatorio',
          empresa_wide: false,
          cargos_vinculados: []
        });
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    setShowImportModal(false);
    setExtractedTrainings([]);
    setSelectedImports([]);
    fetchData();
    
    if (successCount > 0) {
      toast.success(`${successCount} treinamento(s) importado(s) com sucesso!`);
    }
    if (errorCount > 0) {
      toast.warning(`${errorCount} treinamento(s) não puderam ser importados`);
    }
    
    setImporting(false);
  };

  const toggleImportSelection = (index) => {
    setSelectedImports(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Gerar turmas automaticamente para treinamentos selecionados
  const handleAutoGenerateTurmas = async (trainingIds) => {
    if (!trainingIds || trainingIds.length === 0) {
      toast.error('Selecione pelo menos um treinamento');
      return;
    }
    
    setGeneratingTurmas(true);
    try {
      const res = await turmasAPI.autoGenerate(trainingIds);
      const results = res.data?.resultados || [];
      
      let turmasCriadas = 0;
      let funcionariosInscritos = 0;
      
      results.forEach(r => {
        if (r.turma_criada) turmasCriadas++;
        funcionariosInscritos += r.funcionarios_inscritos || 0;
      });
      
      if (turmasCriadas > 0 || funcionariosInscritos > 0) {
        toast.success(`${turmasCriadas} turma(s) criada(s), ${funcionariosInscritos} funcionário(s) inscrito(s)`);
      } else {
        toast.info('Nenhuma turma precisou ser criada ou todos os funcionários já estão inscritos');
      }
      
      fetchData();
    } catch (error) {
      toast.error('Erro ao gerar turmas automaticamente');
    } finally {
      setGeneratingTurmas(false);
    }
  };

  // Criar nova turma
  const handleCreateTurma = async () => {
    if (!turmaForm.training_id) {
      toast.error('Selecione um treinamento');
      return;
    }
    
    try {
      // Criar turma para um treinamento específico
      await turmasAPI.create({
        training_id: turmaForm.training_id,
        nome: turmaForm.nome,
        data_inicio: turmaForm.data_inicio || null,
        data_fim: turmaForm.data_fim || null,
        instrutor: turmaForm.instrutor,
        local: turmaForm.local,
        local_detalhes: turmaForm.local_detalhes,
        capacidade_maxima: parseInt(turmaForm.capacidade_maxima) || 30,
        observacoes: turmaForm.observacoes
      });
      toast.success('Turma criada com sucesso!');
      
      setShowTurmaModal(false);
      setTurmaForm({
        training_id: '',
        nome: '',
        data_inicio: '',
        data_fim: '',
        instrutor: '',
        local: 'presencial',
        local_detalhes: '',
        capacidade_maxima: 30,
        observacoes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar turma');
    }
  };

  const handleViewExpiring = async (training) => {
    try {
      const res = await trainingsAPI.getExpiring(training.id, 30);
      setExpiringData(res.data);
      setShowExpiringModal(true);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const resetTrainingForm = () => {
    setTrainingForm({
      nome: '',
      descricao: '',
      categoria: 'obrigatorio',
      carga_horaria: '',
      validade_meses: '',
      obrigatorio: true,
      empresa_wide: false,
      cargos_vinculados: [],
      requisitos_legais: '',
      fornecedor: '',
      custo_estimado: ''
    });
    setEditingTraining(null);
  };

  const resetRegisterForm = () => {
    setRegisterForm({
      employee_id: '',
      training_id: '',
      data_realizacao: '',
      certificado_url: '',
      observacoes: '',
      nota: ''
    });
  };

  const openEditModal = (training) => {
    setEditingTraining(training);
    setTrainingForm({
      nome: training.nome,
      descricao: training.descricao || '',
      categoria: training.categoria,
      carga_horaria: training.carga_horaria?.toString() || '',
      validade_meses: training.validade_meses?.toString() || '',
      obrigatorio: training.obrigatorio,
      empresa_wide: training.empresa_wide,
      cargos_vinculados: training.cargos_vinculados || [],
      requisitos_legais: training.requisitos_legais || '',
      fornecedor: training.fornecedor || '',
      custo_estimado: training.custo_estimado?.toString() || ''
    });
    setShowTrainingModal(true);
  };

  const filteredTrainings = trainings.filter(t => {
    const matchesSearch = t.nome.toLowerCase().includes(search.toLowerCase());
    const matchesCategoria = filterCategoria === 'all' || t.categoria === filterCategoria;
    return matchesSearch && matchesCategoria;
  });

  // Stats
  const totalTreinamentos = trainings.length;
  const totalObrigatorios = trainings.filter(t => t.obrigatorio).length;
  const totalVencendo = trainings.reduce((acc, t) => acc + (t.total_vencendo_30_dias || 0), 0);

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="trainings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestão de Treinamentos</h1>
            <p className="text-slate-600">Controle de treinamentos obrigatórios e eventuais</p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-[600px] bg-slate-100/50 p-1">
            <TabsTrigger 
              value="trainings" 
              className="flex items-center gap-2 data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700 data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 hover:bg-cyan-50/50"
            >
              <GraduationCap className="w-4 h-4" />
              Treinamentos
            </TabsTrigger>
            <TabsTrigger 
              value="qualificacao" 
              className="flex items-center gap-2 data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 data-[state=active]:border-b-2 data-[state=active]:border-violet-500 hover:bg-violet-50/50"
            >
              <UserCheck className="w-4 h-4" />
              Qualificação
            </TabsTrigger>
            <TabsTrigger 
              value="lnt" 
              className="flex items-center gap-2 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 hover:bg-amber-50/50"
            >
              <AlertTriangle className="w-4 h-4" />
              LNT
            </TabsTrigger>
            <TabsTrigger 
              value="turmas" 
              className="flex items-center gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 hover:bg-emerald-50/50"
            >
              <ClipboardList className="w-4 h-4" />
              Turmas
            </TabsTrigger>
          </TabsList>

          {/* Tab: Treinamentos */}
          <TabsContent value="trainings" className="space-y-6 mt-6">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline"
                onClick={handleExtractFromDescriptions}
                disabled={importing}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="extract-trainings-btn"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Importar das Descrições
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCargoView(true)}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="cargo-view-btn"
              >
                <Layers className="w-4 h-4 mr-2" />
                Visão por Cargo
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowReclassify(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="reclassify-btn"
              >
                <ListChecks className="w-4 h-4 mr-2" />
                Revisar Classificação
              </Button>
              <Button 
                onClick={() => { resetTrainingForm(); setShowTrainingModal(true); }}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="new-training-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Treinamento
              </Button>
              <Button 
                variant="outline"
                onClick={() => { resetRegisterForm(); setShowRegisterModal(true); }}
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Registrar Realização
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowTurmaModal(true)}
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                <Users className="w-4 h-4 mr-2" />
                Nova Turma
              </Button>
            </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <GraduationCap className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalTreinamentos}</p>
                  <p className="text-sm text-slate-600">Treinamentos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalObrigatorios}</p>
                  <p className="text-sm text-slate-600">Obrigatórios</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{turmas.length}</p>
                  <p className="text-sm text-slate-600">Turmas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {turmas.filter(t => t.status === 'aberta').length}
                  </p>
                  <p className="text-sm text-slate-600">Turmas Abertas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={totalVencendo > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${totalVencendo > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${totalVencendo > 0 ? 'text-red-600' : 'text-slate-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${totalVencendo > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {totalVencendo}
                  </p>
                  <p className="text-sm text-slate-600">Vencendo em 30 dias</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar treinamento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategoria} onValueChange={setFilterCategoria}>
            <SelectTrigger className="w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {Object.entries(CATEGORIAS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Trainings Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Treinamento</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-center">Validade</TableHead>
                  <TableHead className="text-center">Obrigatório Para</TableHead>
                  <TableHead className="text-center">Conformidade</TableHead>
                  <TableHead className="text-center">Vencendo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTrainings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Nenhum treinamento encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTrainings.map((training) => {
                    const conformidade = training.total_funcionarios_requeridos > 0
                      ? Math.round((training.total_funcionarios_treinados / training.total_funcionarios_requeridos) * 100)
                      : 100;
                    
                    return (
                      <TableRow key={training.id} data-testid={`training-row-${training.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{training.nome}</p>
                            {training.revisao_classificacao && (
                              <Badge className="bg-amber-100 text-amber-700 text-[10px] mt-1" title={training.revisao_classificacao.motivo} data-testid={`training-revisao-badge-${training.id}`}>
                                Revisar classificação
                              </Badge>
                            )}
                            {training.requisitos_legais && (
                              <p className="text-xs text-slate-500">{training.requisitos_legais}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{CATEGORIAS[training.categoria] || training.categoria}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {training.validade_meses ? (
                            <span className="text-sm">{training.validade_meses} meses</span>
                          ) : (
                            <span className="text-sm text-slate-400">Não expira</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {training.empresa_wide ? (
                            <Badge className="bg-purple-100 text-purple-700">Toda Empresa</Badge>
                          ) : training.cargos_nomes?.length > 0 ? (
                            <span className="text-sm text-slate-600">
                              {training.cargos_nomes.slice(0, 2).join(', ')}
                              {training.cargos_nomes.length > 2 && ` +${training.cargos_nomes.length - 2}`}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Progress value={conformidade} className="w-16 h-2" />
                            <span className={`text-sm font-medium ${
                              conformidade >= 80 ? 'text-green-600' : conformidade >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {conformidade}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {training.total_vencendo_30_dias > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewExpiring(training)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              {training.total_vencendo_30_dias}
                            </Button>
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(training)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(training)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Turmas Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  Turmas de Treinamento
                </CardTitle>
                <CardDescription>Gerencie turmas e inscrições de funcionários</CardDescription>
              </div>
              <Button
                onClick={() => handleAutoGenerateTurmas(trainings.filter(t => t.obrigatorio).map(t => t.id))}
                disabled={generatingTurmas}
                className="bg-green-600 hover:bg-green-700"
                size="sm"
              >
                {generatingTurmas ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Users className="w-4 h-4 mr-2" />
                )}
                Gerar Turmas Auto
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {turmas.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhuma turma cadastrada</p>
                <p className="text-sm mt-1">Crie turmas para organizar os treinamentos dos funcionários</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {turmas.map((turma) => (
                  <Card key={turma.id} className="border border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate">{turma.nome}</h4>
                          <p className="text-xs text-slate-500 truncate">{turma.training_nome}</p>
                        </div>
                        <Badge 
                          variant={turma.status === 'aberta' ? 'outline' : turma.status === 'concluida' ? 'success' : 'secondary'}
                          className={
                            turma.status === 'aberta' ? 'border-green-500 text-green-700' :
                            turma.status === 'concluida' ? 'bg-green-100 text-green-800' :
                            turma.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-600'
                          }
                        >
                          {turma.status === 'aberta' ? 'Aberta' : 
                           turma.status === 'concluida' ? 'Concluída' :
                           turma.status === 'em_andamento' ? 'Em Andamento' : 
                           turma.status}
                        </Badge>
                      </div>
                      
                      <div className="space-y-1 text-xs text-slate-600 mb-3">
                        {turma.data_inicio ? (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(turma.data_inicio).toLocaleDateString('pt-BR')}</span>
                            {turma.data_fim && <span> - {new Date(turma.data_fim).toLocaleDateString('pt-BR')}</span>}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-amber-600">
                            <Clock className="w-3 h-3" />
                            <span>Data a definir</span>
                          </div>
                        )}
                        {turma.instrutor && (
                          <div className="flex items-center gap-1">
                            <GraduationCap className="w-3 h-3" />
                            <span>{turma.instrutor}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{turma.participantes_count}</span>
                          <span className="text-slate-400">/ {turma.capacidade_maxima}</span>
                        </div>
                        <div className="text-xs text-green-600">
                          {turma.vagas_disponiveis} vagas
                        </div>
                      </div>
                      
                      <Progress 
                        value={(turma.participantes_count / turma.capacidade_maxima) * 100} 
                        className="h-1 mt-2"
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

          </TabsContent>

          {/* Tab: Qualificação */}
          <TabsContent value="qualificacao" className="mt-6">
            <QualificacaoTab />
          </TabsContent>

          {/* Tab: LNT - Levantamento de Necessidade de Treinamento */}
          <TabsContent value="lnt" className="mt-6">
            <LNTTab onCreateTurma={(trainingId, employeeIds) => {
              // Pre-fill turma modal with selected training and employees
              setTurmaForm(prev => ({
                ...prev,
                training_id: trainingId,
                participantes_selecionados: employeeIds
              }));
              setShowTurmaModal(true);
            }} />
          </TabsContent>

          {/* Tab: Turmas */}
          <TabsContent value="turmas" className="mt-6">
            <TurmasTab />
          </TabsContent>
        </Tabs>

        {/* Training Modal */}
        <Dialog open={showTrainingModal} onOpenChange={setShowTrainingModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTraining ? 'Editar Treinamento' : 'Novo Treinamento'}
              </DialogTitle>
              <DialogDescription>
                Configure os detalhes do treinamento e a quem ele se aplica
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="nome">Nome do Treinamento *</Label>
                  <Input
                    id="nome"
                    name="training_name_unique"
                    autoComplete="off"
                    value={trainingForm.nome}
                    onChange={(e) => setTrainingForm({ ...trainingForm, nome: e.target.value })}
                    placeholder="Ex: NR-35 Trabalho em Altura"
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={trainingForm.descricao}
                    onChange={(e) => setTrainingForm({ ...trainingForm, descricao: e.target.value })}
                    placeholder="Descrição e objetivos do treinamento"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Categoria</Label>
                  <Select 
                    value={trainingForm.categoria} 
                    onValueChange={(v) => setTrainingForm({ ...trainingForm, categoria: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIAS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="carga_horaria">Carga Horária (horas)</Label>
                  <Input
                    id="carga_horaria"
                    type="number"
                    value={trainingForm.carga_horaria}
                    onChange={(e) => setTrainingForm({ ...trainingForm, carga_horaria: e.target.value })}
                    placeholder="Ex: 8"
                  />
                </div>

                <div>
                  <Label htmlFor="validade_meses">Validade (meses)</Label>
                  <Input
                    id="validade_meses"
                    type="number"
                    value={trainingForm.validade_meses}
                    onChange={(e) => setTrainingForm({ ...trainingForm, validade_meses: e.target.value })}
                    placeholder="Ex: 24 (deixe vazio se não expira)"
                  />
                </div>

                <div>
                  <Label htmlFor="requisitos_legais">Requisito Legal</Label>
                  <Input
                    id="requisitos_legais"
                    value={trainingForm.requisitos_legais}
                    onChange={(e) => setTrainingForm({ ...trainingForm, requisitos_legais: e.target.value })}
                    placeholder="Ex: NR-35, NR-10"
                  />
                </div>

                <div className="sm:col-span-2 flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="obrigatorio"
                      checked={trainingForm.obrigatorio}
                      onCheckedChange={(checked) => setTrainingForm({ ...trainingForm, obrigatorio: checked })}
                    />
                    <Label htmlFor="obrigatorio">Obrigatório</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="empresa_wide"
                      checked={trainingForm.empresa_wide}
                      onCheckedChange={(checked) => setTrainingForm({ ...trainingForm, empresa_wide: checked })}
                    />
                    <Label htmlFor="empresa_wide">Para toda a empresa</Label>
                  </div>
                </div>

                {!trainingForm.empresa_wide && (
                  <div className="sm:col-span-2">
                    <Label>Cargos que requerem este treinamento</Label>
                    <div className="mt-2 border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {positions.map((pos) => (
                        <label key={pos.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={trainingForm.cargos_vinculados.includes(pos.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTrainingForm({
                                  ...trainingForm,
                                  cargos_vinculados: [...trainingForm.cargos_vinculados, pos.id]
                                });
                              } else {
                                setTrainingForm({
                                  ...trainingForm,
                                  cargos_vinculados: trainingForm.cargos_vinculados.filter(id => id !== pos.id)
                                });
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{pos.titulo}</span>
                        </label>
                      ))}
                      {positions.length === 0 && (
                        <p className="text-sm text-slate-500">Nenhum cargo cadastrado</p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="fornecedor">Fornecedor</Label>
                  <Input
                    id="fornecedor"
                    value={trainingForm.fornecedor}
                    onChange={(e) => setTrainingForm({ ...trainingForm, fornecedor: e.target.value })}
                    placeholder="Nome do fornecedor"
                  />
                </div>

                <div>
                  <Label htmlFor="custo_estimado">Custo Estimado (R$)</Label>
                  <Input
                    id="custo_estimado"
                    type="number"
                    step="0.01"
                    value={trainingForm.custo_estimado}
                    onChange={(e) => setTrainingForm({ ...trainingForm, custo_estimado: e.target.value })}
                    placeholder="Ex: 500.00"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTrainingModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveTraining} className="bg-cyan-600 hover:bg-cyan-700">
                {editingTraining ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Register Training Modal */}
        <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
          <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Registrar Treinamento Realizado</DialogTitle>
              <DialogDescription>
                Registre a realização de um treinamento por um funcionário
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 py-3 overflow-y-auto flex-1 pr-1">
              <div>
                <Label className="text-sm">Funcionário *</Label>
                <Select 
                  value={registerForm.employee_id} 
                  onValueChange={(v) => setRegisterForm({ ...registerForm, employee_id: v })}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Selecione o funcionário" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id} className="text-sm">
                        {emp.nome} - {emp.cargo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Treinamento *</Label>
                <Select 
                  value={registerForm.training_id} 
                  onValueChange={(v) => setRegisterForm({ ...registerForm, training_id: v })}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Selecione o treinamento" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {trainings.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-sm">
                        {t.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="data_realizacao" className="text-sm">Data de Realização *</Label>
                <Input
                  id="data_realizacao"
                  type="date"
                  value={registerForm.data_realizacao}
                  onChange={(e) => setRegisterForm({ ...registerForm, data_realizacao: e.target.value })}
                  className="text-sm"
                />
              </div>

              <div>
                <Label htmlFor="nota" className="text-sm">Nota/Aproveitamento (%)</Label>
                <Input
                  id="nota"
                  type="number"
                  min="0"
                  max="100"
                  value={registerForm.nota}
                  onChange={(e) => setRegisterForm({ ...registerForm, nota: e.target.value })}
                  placeholder="Ex: 85"
                  className="text-sm"
                />
              </div>

              <div>
                <Label htmlFor="observacoes" className="text-sm">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={registerForm.observacoes}
                  onChange={(e) => setRegisterForm({ ...registerForm, observacoes: e.target.value })}
                  placeholder="Observações sobre o treinamento"
                  rows={2}
                  className="text-sm"
                />
              </div>
            </div>

            <DialogFooter className="pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setShowRegisterModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleRegisterTraining} size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Expiring Trainings Modal */}
        <Dialog open={showExpiringModal} onOpenChange={setShowExpiringModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Treinamentos Vencendo
              </DialogTitle>
              <DialogDescription>
                {expiringData?.training_nome} - Funcionários com treinamento vencendo nos próximos 30 dias
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Funcionário</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead className="text-center">Data Realização</TableHead>
                    <TableHead className="text-center">Validade</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expiringData?.funcionarios?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        Nenhum treinamento vencendo
                      </TableCell>
                    </TableRow>
                  ) : (
                    expiringData?.funcionarios?.map((func, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{func.employee_nome}</TableCell>
                        <TableCell>{func.employee_cargo}</TableCell>
                        <TableCell className="text-center">
                          {func.data_realizacao ? new Date(func.data_realizacao).toLocaleDateString('pt-BR') : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {func.data_validade ? new Date(func.data_validade).toLocaleDateString('pt-BR') : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {func.status === 'vencido' ? (
                            <Badge className="bg-red-100 text-red-700">Vencido</Badge>
                          ) : func.dias_para_vencer <= 0 ? (
                            <Badge className="bg-red-100 text-red-700">Vencido</Badge>
                          ) : func.dias_para_vencer <= 15 ? (
                            <Badge className="bg-amber-100 text-amber-700">{func.dias_para_vencer} dias</Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700">{func.dias_para_vencer} dias</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExpiringModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Importar Treinamentos das Descrições */}
        <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Importar Treinamentos das Descrições de Cargo
              </DialogTitle>
              <DialogDescription>
                Treinamentos encontrados nas descrições de cargos cadastradas. Selecione os que deseja importar.
              </DialogDescription>
              <p className="text-xs text-slate-500 mt-1" data-testid="import-ignored-info">
                {ignoredCounts.requisitos} requisito(s) do cargo e {ignoredCounts.competencias} habilidade(s)/competência(s) foram identificados e não serão importados como treinamento.
                Consulte em &quot;Visão por Cargo&quot;.
              </p>
            </DialogHeader>

            <div className="flex-1 overflow-hidden py-4">
              {extractedTrainings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nenhum novo treinamento encontrado nas descrições de cargo</p>
                  <p className="text-sm mt-1">Todos os treinamentos já estão cadastrados ou não há treinamentos nas descrições.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-slate-600">
                      <strong>{extractedTrainings.length}</strong> treinamento(s) encontrado(s) | 
                      <strong className="text-purple-600"> {selectedImports.length}</strong> selecionado(s)
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedImports(extractedTrainings.map((_, i) => i))}
                      >
                        Selecionar todos
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedImports([])}
                      >
                        Limpar seleção
                      </Button>
                    </div>
                  </div>
                  
                  <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-2">
                    {extractedTrainings.map((training, index) => (
                      <div 
                        key={index}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedImports.includes(index) 
                            ? 'border-purple-400 bg-purple-50' 
                            : 'border-slate-200 hover:border-purple-200'
                        }`}
                        onClick={() => toggleImportSelection(index)}
                      >
                        <div className="flex items-start gap-3">
                          <input 
                            type="checkbox"
                            checked={selectedImports.includes(index)}
                            onChange={() => toggleImportSelection(index)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900">{training.nome}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {CATEGORIAS[training.categoria] || training.categoria}
                              </Badge>
                              <span className="text-xs text-slate-500">
                                Origem: {training.origem}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmImport}
                disabled={importing || selectedImports.length === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Importar {selectedImports.length} Treinamento(s)
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Nova Turma */}
        <Dialog open={showTurmaModal} onOpenChange={setShowTurmaModal}>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Nova Turma de Treinamento
              </DialogTitle>
              <DialogDescription>
                Crie uma turma para organizar o treinamento dos funcionários.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4 overflow-y-auto flex-1 pr-1">
              {/* Seleção de Treinamento */}
              <div>
                <Label>Treinamento *</Label>
                <Select 
                  value={turmaForm.training_id} 
                  onValueChange={(v) => setTurmaForm({ ...turmaForm, training_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o treinamento" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {trainings.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Campos adicionais */}
              {turmaForm.training_id && (
                <>
                  <div>
                    <Label>Nome da Turma</Label>
                    <Input
                      value={turmaForm.nome}
                      onChange={(e) => setTurmaForm({ ...turmaForm, nome: e.target.value })}
                      placeholder="Auto-gerado se deixar em branco"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data de Início</Label>
                      <Input
                        type="date"
                        value={turmaForm.data_inicio}
                        onChange={(e) => setTurmaForm({ ...turmaForm, data_inicio: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Data de Término</Label>
                      <Input
                        type="date"
                        value={turmaForm.data_fim}
                        onChange={(e) => setTurmaForm({ ...turmaForm, data_fim: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Instrutor / Facilitador</Label>
                    <Input
                      value={turmaForm.instrutor}
                      onChange={(e) => setTurmaForm({ ...turmaForm, instrutor: e.target.value })}
                      placeholder="Nome do instrutor"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Modalidade</Label>
                      <Select 
                        value={turmaForm.local} 
                        onValueChange={(v) => setTurmaForm({ ...turmaForm, local: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="presencial">Presencial</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                          <SelectItem value="hibrido">Híbrido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Capacidade Máxima</Label>
                      <Input
                        type="number"
                        min="1"
                        value={turmaForm.capacidade_maxima}
                        onChange={(e) => setTurmaForm({ ...turmaForm, capacidade_maxima: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Local / Link</Label>
                    <Input
                      value={turmaForm.local_detalhes}
                      onChange={(e) => setTurmaForm({ ...turmaForm, local_detalhes: e.target.value })}
                      placeholder="Endereço ou link da plataforma"
                    />
                  </div>

                  <div>
                    <Label>Observações</Label>
                    <Textarea
                      value={turmaForm.observacoes}
                      onChange={(e) => setTurmaForm({ ...turmaForm, observacoes: e.target.value })}
                      placeholder="Informações adicionais"
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="pt-2 border-t">
              <Button variant="outline" onClick={() => setShowTurmaModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateTurma}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Turma
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <CargoClassificationDialog open={showCargoView} onClose={() => setShowCargoView(false)} />
        <TrainingReclassifyDialog
          open={showReclassify}
          onClose={() => setShowReclassify(false)}
          onApplied={fetchData}
        />
      </div>
    </DashboardLayout>
  );
}
